import React, { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { getAllStores as fetchAllStoresFromApi, getUserStores as fetchStoresFromApi } from '../services/storeService';
import { ApiErrorResponse } from '../types/auth';
import { Store } from '../types/store';
import { useAuth } from './AuthContext';

export type { Store };

interface StoreContextType {
  selectedStore: Store | null;
  userStores: Store[];
  isLoadingStores: boolean;
  errorFetchingStores: string | null;
  selectStore: (store: Store | null) => void;
  fetchUserStores: () => Promise<void>;
  clearStoreContext: () => void;
  addStoreToList: (newStore: Store) => void;
  updateStoreInList: (updatedStore: Store) => void;
}

const StoreContext = createContext<StoreContextType | undefined>(undefined);

export const StoreProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { currentUser, token, isLoading: isAuthLoading } = useAuth();
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [userStores, setUserStoresState] = useState<Store[]>([]);
  const [isLoadingStores, setIsLoadingStores] = useState<boolean>(false);
  const [errorFetchingStores, setErrorFetchingStores] = useState<string | null>(null);

  const selectStore = (store: Store | null) => {
    setSelectedStore(store);
    if (store) {
      localStorage.setItem('selectedStoreId', store.id.toString());
    } else {
      localStorage.removeItem('selectedStoreId');
    }
  };

  const fetchUserStores = useCallback(async () => {
    if (!token || !currentUser) {
      setUserStoresState([]);
      setSelectedStore(null);
      localStorage.removeItem('selectedStoreId');
      return;
    }
    setIsLoadingStores(true);
    setErrorFetchingStores(null);
    try {
      const isAdmin = currentUser.role?.role_name?.toLowerCase() === 'admin';
      const result = isAdmin
        ? await fetchAllStoresFromApi()
        : await fetchStoresFromApi();
      const storesFromApi = result.items;
      setUserStoresState(storesFromApi);

      const storedSelectedStoreId = localStorage.getItem('selectedStoreId');
      if (storesFromApi.length > 0) {
        if (storedSelectedStoreId) {
          const foundStore = storesFromApi.find((s: Store) => s.id === parseInt(storedSelectedStoreId, 10));
          if (foundStore) {
            setSelectedStore(foundStore);
          } else {
            setSelectedStore(storesFromApi[0]);
            localStorage.setItem('selectedStoreId', storesFromApi[0].id.toString());
          }
        } else {
          setSelectedStore(storesFromApi[0]);
          localStorage.setItem('selectedStoreId', storesFromApi[0].id.toString());
        }
      } else {
        setSelectedStore(null);
        localStorage.removeItem('selectedStoreId');
      }

    } catch (err) {
      const apiError = err as ApiErrorResponse;
      setErrorFetchingStores(apiError.message || 'Could not load your stores.');
      setUserStoresState([]);
      setSelectedStore(null);
      localStorage.removeItem('selectedStoreId');
    } finally {
      setIsLoadingStores(false);
    }
  }, [token, currentUser]);

  useEffect(() => {
    if (!isAuthLoading && currentUser && token) {
      fetchUserStores();
    } else if (!isAuthLoading && (!currentUser || !token)) {
      setUserStoresState([]);
      setSelectedStore(null);
      localStorage.removeItem('selectedStoreId');
      setIsLoadingStores(false);
    }
  }, [currentUser, token, isAuthLoading, fetchUserStores]);

  const clearStoreContext = () => {
    setSelectedStore(null);
    setUserStoresState([]);
    setIsLoadingStores(false);
    setErrorFetchingStores(null);
    localStorage.removeItem('selectedStoreId');
  };

  const addStoreToList = (newStore: Store) => {
    setUserStoresState(prevStores => [...prevStores, newStore]);
  };

  const updateStoreInList = (updatedStore: Store) => {
    setUserStoresState(prevStores =>
      prevStores.map(store => store.id === updatedStore.id ? updatedStore : store)
    );
    if (selectedStore && selectedStore.id === updatedStore.id) {
      setSelectedStore(updatedStore);
    }
  };

  return (
    <StoreContext.Provider value={{
      selectedStore,
      userStores,
      isLoadingStores,
      errorFetchingStores,
      selectStore,
      fetchUserStores,
      clearStoreContext,
      addStoreToList,
      updateStoreInList
    }}>
      {children}
    </StoreContext.Provider>
  );
};

export const useStore = (): StoreContextType => {
  const context = useContext(StoreContext);
  if (context === undefined) {
    throw new Error('useStore must be used within a StoreProvider');
  }
  return context;
}; 